import { type AppLocale, DEFAULT_LOCALE } from "./terminology";

/**
 * Parse an Accept-Language or navigator.language value into an AppLocale.
 * Returns "en-GB" for British/Irish/Australian/NZ/South African English,
 * "en-US" for everything else (American default).
 */
const GB_REGIONS = new Set([
  "gb",
  "ie",
  "au",
  "nz",
  "za",
  "in",
  "pk",
  "ng",
  "ke",
  "gh",
  "sg",
  "hk",
  "my",
]);

export function parseLocale(raw: string | null | undefined): AppLocale {
  if (!raw) return DEFAULT_LOCALE;

  // Accept-Language can be "en-GB,en;q=0.9,fr;q=0.8" — take the first entry
  const primary = raw.split(",")[0].trim().toLowerCase();

  // Match "en-gb", "en-au", etc.
  const match = primary.match(/^en-(\w+)/);
  if (match) {
    return GB_REGIONS.has(match[1]) ? "en-GB" : "en-US";
  }

  // Plain "en" → default
  if (primary.startsWith("en")) return DEFAULT_LOCALE;

  // Non-English → default to US English
  return DEFAULT_LOCALE;
}

/**
 * Detect locale on the client side from navigator.language.
 */
export function detectClientLocale(): AppLocale {
  if (typeof navigator === "undefined") return DEFAULT_LOCALE;
  return parseLocale(navigator.language);
}

/**
 * Detect locale from a server-side request headers object.
 * Works with Next.js `headers()` from next/headers.
 */
export function detectServerLocale(
  headerValue: string | null | undefined,
): AppLocale {
  return parseLocale(headerValue);
}
